import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { Lock } from 'phosphor-react';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { PERFORMANCE_CURRENCIES, PERFORMANCE_RANGES } from './optionsPerformanceUtils';
import { getRangeLabel } from './optionsDashboardUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsCurrencyDisplay } from './OptionsCurrencyDisplay';
import { OptionsSyncButton } from './OptionsSyncButton';
import { isOptionsCustomPeriodRestricted } from './optionsFreemiumUtils';

export const OptionsPerformanceControls = ({
  selectedCurrency,
  onCurrencyChange,
  selectedRange,
  onRangeChange,
  customRange,
  onCustomRangeChange,
  onApplyCustomRange,
  onSync,
  syncing,
  showTestnetBadge,
  showSyncButton = true,
  isFetching = false,
}) => {
  const { t } = useOptionsLocale();
  const { isPro, openUpgradeModal } = useOptionsEntitlements();
  return (
    <div className="space-y-3" data-testid="options-performance-controls">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap items-center gap-3" data-testid="options-performance-currency-selector">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40" data-typography="label" data-testid="options-performance-currency-label">{t('common.view')}</span>
          <div className="inline-flex flex-wrap items-center gap-1.5 rounded-[18px] border border-white/10 bg-[#11161E] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]" data-testid="options-performance-currency-tabs">
            {PERFORMANCE_CURRENCIES.map((currency) => {
              const isActive = selectedCurrency === currency;
              return (
                <button
                  key={currency}
                  type="button"
                  onClick={() => onCurrencyChange(currency)}
                  data-testid={`options-performance-currency-${currency}`}
                  className={`inline-flex items-center rounded-[14px] px-3 py-2 text-sm font-semibold transition-all duration-200 font-body ${isActive ? 'bg-[#202A38] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]' : 'text-white/62 hover:bg-white/[0.04] hover:text-white'}`}
                  data-typography="nav"
                >
                  <OptionsCurrencyDisplay currencyKey={currency} compact iconSize={16} />
                </button>
              );
            })}
          </div>
          {showTestnetBadge ? (
            <span className="rounded-full border border-[#F0B90B]/20 bg-[#F0B90B]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#F7D47A]" data-testid="options-performance-testnet-badge">
              {t('common.testnet')}
            </span>
          ) : null}
        </div>

        {showSyncButton ? (
          <div className="flex flex-wrap items-center gap-3" data-testid="options-performance-sync-area">
            <OptionsSyncButton onSync={onSync} syncing={syncing} testId="options-performance-sync-button" />
          </div>
        ) : null}
      </div>

      <div className={`flex flex-col gap-2 lg:flex-row lg:items-center ${selectedRange === 'custom' ? 'lg:justify-between' : 'lg:justify-start'}`} data-testid="options-performance-range-row">
        <div className="flex flex-wrap items-center gap-3" data-testid="options-performance-range-selector">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40" data-typography="label" data-testid="options-performance-range-label">{t('common.period')}</span>
          {isFetching ? (
            <RefreshCw size={12} className="animate-spin text-white/40" data-testid="options-performance-range-fetching-indicator" />
          ) : null}
          <div className="inline-flex flex-wrap rounded-2xl border border-white/10 bg-white/[0.03] p-1" data-testid="options-performance-range-tabs">
            {PERFORMANCE_RANGES.map((range) => {
              const isActive = selectedRange === range;
              const isRestricted = isOptionsCustomPeriodRestricted({ isPro, range });
              
              const handleClick = () => {
                if (isRestricted) {
                  openUpgradeModal("PERFORMANCE");
                  return;
                }
                onRangeChange(range);
              };

              return (
                <button
                  key={range}
                  type="button"
                  onClick={handleClick}
                  data-testid={`options-performance-range-${range}`}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-colors duration-200 font-body ${isActive ? 'bg-white text-black' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
                  data-typography="nav"
                >
                  {isRestricted && <Lock size={12} className="text-[#A855F7]" weight="fill" />}
                  {getRangeLabel(range, t)}
                  {isRestricted && <span className="text-[9px] bg-[#A855F7]/10 text-[#A855F7] px-1.5 py-0.5 rounded-sm font-bold">PRO</span>}
                </button>
              );
            })}
          </div>
        </div>

        {selectedRange === 'custom' ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="options-performance-custom-range-controls">
            <input
              type="date"
              value={customRange.startDate}
              onChange={(event) => onCustomRangeChange((current) => ({ ...current, startDate: event.target.value }))}
              className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none"
              data-testid="options-performance-custom-range-start"
            />
            <input
              type="date"
              value={customRange.endDate}
              onChange={(event) => onCustomRangeChange((current) => ({ ...current, endDate: event.target.value }))}
              className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none"
              data-testid="options-performance-custom-range-end"
            />
            <Button variant="outline" onClick={onApplyCustomRange} data-testid="options-performance-custom-range-apply">
              {t('common.apply')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OptionsPerformanceControls;